import type { ReactNode } from 'react';
import type { ComposePlanV1, ComposedSynthesisResultV1 } from '@djgg/creations';
import { useAuth } from '../../services/context.js';
import { RETRY_LABEL, plainComposedFailure, plainPlanText } from './creatorCopy.js';

export type ComposedStageStatus = 'pending' | 'running' | 'done' | 'error';

export interface ComposedStageView {
  /** `plan`, `step-<n>`, `synthesis` or `build`. */
  id: string;
  label: string;
  status: ComposedStageStatus;
}

/** The stored failure text, for admins only. */
export function AdminErrorDetail({ detail }: { detail: string }) {
  const { user } = useAuth();
  if (user?.role !== 'superadmin') return null;
  return <details data-testid="admin-error-detail"><summary>Technical details (admins only)</summary><pre style={{ whiteSpace: 'pre-wrap' }}>{detail}</pre></details>;
}

/** Create's one failure notice and one retry control. */
export function ComposedRunFailure({ stageLabel, detail, onRetry }: {
  stageLabel?: string; detail: string; onRetry: () => void;
}) {
  return <div role="alert" data-testid="composed-error">
    <p>{plainComposedFailure(stageLabel && plainPlanText(stageLabel))}</p>
    <button type="button" className="btn btn--primary" onClick={onRetry} data-testid="composed-retry">{RETRY_LABEL}</button>
    <AdminErrorDetail detail={detail} />
  </div>;
}

/** A run that has used its retries: no retry control, only what to do instead. */
export function ComposedRetryLimit({ message, children }: { message: string; children?: ReactNode }) {
  return <div role="alert" data-testid="composed-error" data-retry-limit="true"><p>{message}</p>{children}</div>;
}

/**
 * Composed Create's progress: the stages the wizard runs by itself (plan,
 * the plan's steps, synthesis, build), what the plan builds from the idea
 * (read-only). Nothing here asks the creator to choose; a failure and its
 * retry are `ComposedRunFailure`, on the page.
 */
export function ComposedCreateProgress({ plan, templateTitle, stages, result }: {
  plan: ComposePlanV1 | null;
  /** The plan's base template, by its catalog title (null for a new game). */
  templateTitle: string | null;
  stages: readonly ComposedStageView[];
  result: ComposedSynthesisResultV1 | null;
}) {
  const running = stages.find(stage => stage.status === 'running');
  return <section id="composed-create-progress" tabIndex={-1} aria-label="Building your game" data-testid="composed-create-progress" style={{ scrollMarginTop: 100 }}>
    <h3>Building your game from the platform’s proven parts</h3>
    {plan && <article data-testid="composed-plan">
      <p>{plainPlanText(plan.explanation)}</p>
      {templateTitle && <p data-testid="composed-plan-base"><strong>Starting from:</strong> {templateTitle}</p>}
      {plan.included.length > 0 && <><p><strong>Included from your idea:</strong></p>
        <ul data-testid="composed-plan-included">{plan.included.map(item => <li key={item}>{plainPlanText(item)}</li>)}</ul></>}
      {plan.notIncluded.length > 0 && <><p><strong>Not included yet:</strong></p>
        <ul data-testid="composed-plan-not-included">{plan.notIncluded.map(item => <li key={item}>{plainPlanText(item)}</li>)}</ul></>}
      {plan.changes.length > 0 && <><p><strong>What changes:</strong></p>
        <ul data-testid="composed-plan-changes">{plan.changes.map(item => <li key={item}>{plainPlanText(item)}</li>)}</ul></>}
      {plan.missing.length > 0 && <><p><strong>New rules written for your game:</strong></p>
        <ul data-testid="composed-plan-missing">{plan.missing.map(need => <li key={need.id}>{plainPlanText(need.need)}</li>)}</ul></>}
    </article>}
    <ol data-testid="composed-stages">
      {stages.map(stage => <li key={stage.id} data-testid={`composed-stage-${stage.id}`} data-status={stage.status}>
        {stage.label}{stage.status === 'running' ? ' — running' : stage.status === 'done' ? ' — completed' : stage.status === 'error' ? ' — failed' : ' — waiting'}
      </li>)}
    </ol>
    {running && <p role="status">The server is still working on {running.label.toLowerCase()}. This page checks the saved progress every five seconds. You can leave and return from Studio without losing completed stages.</p>}
    {result && <p data-testid="composed-proof">
      <strong>Proven playable:</strong> our test bot won your game on {result.synthesis.proof.seeds.length} different starts, and each of
      its {result.synthesis.proof.stubChecked.length} core rules was shown to matter.
    </p>}
  </section>;
}
